// utils/sessionExpiry.js
//
// One place that decides what happens when the server says the session is over.
//
// Detection was never the problem — `errorCode: 7` ("Please login") is already
// recognised by `isSessionEnded`, and every normalised error carries a
// `sessionEnded` flag. The problem was that acting on it was left to each call
// site, and the helpers that *swallow* a failure so the page can still render
// swallowed the signal with it. `getUserShortcuts` returning `[]` was the visible
// case: the dashboard drew an empty shortcut grid, which reads as "my shortcuts
// were deleted" rather than "you are signed out". See OUTSTANDING_ITEMS.md §C2.
//
// So detection stays where it is. The RESPONSE moves here, behind a latch, and
// the layers that already know (`apiPost`, `useApiError`) just report it.
//
// WHY THE LATCH MATTERS
// A dashboard fires several requests at once. When a key expires they fail
// together — and without a latch the user gets one toast and one redirect per
// in-flight request, which is how a stale session turns into a screenful of
// duplicate errors. Only the first notification acts; the rest are no-ops.
//
// This module is deliberately framework-free (no React, no router) so the plain
// utility layer can call it. `AuthProvider` supplies the actual behaviour.

/** @type {((detail: object) => void) | null} */
let handler = null;

/** True once a sign-out has been triggered and not yet reset by a new sign-in. */
let fired = false;

/**
 * Register what should happen when the session ends. `AuthProvider` calls this
 * once; the returned function unregisters, so it can be used as an effect cleanup.
 *
 * Last registration wins. In practice there is exactly one provider.
 *
 * @param {(detail: object) => void} fn
 * @returns {() => void} unregister
 */
export function onSessionEnded(fn) {
    handler = fn;
    return () => { if (handler === fn) handler = null; };
}

/**
 * Report that the server rejected our key. Safe to call from anywhere, any number
 * of times — only the first call in a session does anything.
 *
 * @param {object} [detail] the normalised error, for logging/wording
 * @returns {boolean} true if this call was the one that acted
 */
export function notifySessionEnded(detail = {}) {
    if (fired) return false;
    fired = true;

    if (typeof handler === 'function') {
        // A failure in sign-out must never propagate back into whatever API call
        // happened to notice the expiry first.
        try { handler(detail); } catch { /* best effort */ }
    }

    return true;
}

/**
 * Re-arm the latch. Called when a new key is established, so that a later expiry
 * is acted on again. Also used by tests to isolate cases.
 */
export function resetSessionEnded() {
    fired = false;
}

/** Exposed for tests and assertions; not needed by application code. */
export function hasSessionEndedFired() {
    return fired;
}
